import { z } from 'zod';

import { AttributeDataType } from '@nucleus-commerce/api-client/admin';

import { SKU_CODE } from './sku-rule-form-schema';

const KEY = /^[a-z][a-z0-9_]*$/;
const VALUE_CODE = /^[a-z0-9][a-z0-9_.-]*$/;
const HEX = /^#[0-9a-fA-F]{6}$/;

const label = z
  .string()
  .trim()
  .min(1, { error: 'attributes:validation.labelRequired' })
  .max(120, { error: 'attributes:validation.labelLength' });

/**
 * One attribute as the create dialog and the detail editor edit it. Key and data type stay in
 * the form after creation (shown read-only); the update mapper leaves them out.
 */
export const attributeFormSchema = z.object({
  label,
  key: z
    .string()
    .trim()
    .min(1, { error: 'attributes:validation.keyRequired' })
    .max(64, { error: 'attributes:validation.keyLength' })
    .regex(KEY, { error: 'attributes:validation.keyFormat' }),
  dataType: z.enum(AttributeDataType),
  unit: z.string().trim().max(16, { error: 'attributes:validation.unitLength' }),
  usableAsOption: z.boolean(),
  isFilterable: z.boolean(),
  isSearchable: z.boolean(),
  isComparable: z.boolean(),
  showOnProductPage: z.boolean(),
});

export type AttributeFormInput = z.input<typeof attributeFormSchema>;
export type AttributeFormValues = z.output<typeof attributeFormSchema>;

/** One select or multiselect value; the code is fixed once saved. An empty swatch means none. */
export const attributeValueFormSchema = z.object({
  label,
  value: z
    .string()
    .trim()
    .min(1, { error: 'attributes:validation.codeRequired' })
    .max(120, { error: 'attributes:validation.codeLength' })
    .regex(VALUE_CODE, { error: 'attributes:validation.codeFormat' }),
  swatch: z
    .string()
    .trim()
    .refine((v) => v === '' || HEX.test(v), { error: 'attributes:validation.swatchFormat' }),
  /** A media id drawn as the swatch; a value may carry a color, an image, or both. */
  swatchMediaId: z.string().nullable(),
  /** The abbreviation SKU templates insert (D-161); empty means derive it from the code. */
  skuCode: z
    .string()
    .trim()
    .refine((v) => v === '' || SKU_CODE.test(v), { error: 'attributes:validation.skuCodeFormat' }),
});

export type AttributeValueFormInput = z.input<typeof attributeValueFormSchema>;
export type AttributeValueFormValues = z.output<typeof attributeValueFormSchema>;
